// externals
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

// components
import { Box } from '@interstate/components/Box';
import { Modal } from '@interstate/components/Modal';
import { Typography } from '@interstate/components/Typography';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';

// libraries
import { DealershipPicker, useDealershipSelector } from '@makemydeal/dr-activities-dealership-picker';
import { bootstrapSharedActionCreators, selectedOfferSelectors } from '@makemydeal/dr-shared-store';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { RouteValues } from '@makemydeal/dr-dash-store/routeConstants';
import * as dealerActionsCreators from '@makemydeal/dr-dash-store/actions/dealerActionsCreators';
import * as dealValidationSelectors from '@makemydeal/dr-dash-store/selectors/dealValidationSelectors';

const MODAL_IDENTIFIER = 'dealership-selection-modal';

/**
 * Blocking, in-Manager-View dealership selection. Self-gated: renders only while the deal is
 * incomplete solely because a dealership is missing (quote mode) AND the dashboard route is active.
 */
export const DealershipSelectionModal: React.FC = () => {
    const dispatch = useDispatch();
    const isDashboardRoute = Boolean(useRouteMatch(RouteValues.DASHBOARD));
    const show = useSelector(dealValidationSelectors.getShowDealershipSelectionModal) && isDashboardRoute;
    const dealXgId = useSelector(selectedOfferSelectors.getSelectedOfferDealXgId);
    const { dealerships } = useDealershipSelector();

    const onChangeDealership = useCallback(
        (event: InterstateOnChangeEvent<SelectInputEventValue>) => {
            const selectedDealerMMDId = `${event.target.value}`;

            if (dealXgId) {
                // Existing DXG deal: persist the chosen dealership onto the offer and re-bootstrap once saved
                const selectedDealer = dealerships.find((dealer) => `${dealer.dealerMMDId}` === selectedDealerMMDId);
                if (selectedDealer) {
                    dispatch(dealerActionsCreators.saveDealershipSelection(selectedDealer));
                }
                return;
            }

            // Org launch (no DXG record): re-init the empty quote deal with the selected dealer.
            dispatch(
                bootstrapSharedActionCreators.fetchBootstrapData(MANAGER_VIEW_APP_PREFIX, {
                    loadDraftScenario: true,
                    dealerId: selectedDealerMMDId
                })
            );
        },
        [dispatch, dealXgId, dealerships]
    );

    if (!show) {
        return null;
    }

    return (
        <Modal
            data-testid={MODAL_IDENTIFIER}
            id={MODAL_IDENTIFIER}
            show
            header={<Typography variant="h4">Select a dealership</Typography>}
            size="large"
            sx={{
                [`#ids-modal-close-button-${MODAL_IDENTIFIER}`]: {
                    display: 'none'
                }
            }}
        >
            <Box paddingBottom={6}>
                <Typography data-testid={`${MODAL_IDENTIFIER}-body`} variant="body-md" color="base.color.gray.600" tag="div">
                    We found multiple dealerships for this organization. Select one to continue.
                </Typography>
                <DealershipPicker label="Dealership" onChangeDealership={onChangeDealership} />
            </Box>
        </Modal>
    );
};

export default DealershipSelectionModal;
